import { createContext, useContext } from 'react';

/**
 * Stable module-level fallback used when a fragment is rendered without a
 * provider above it (e.g. in isolation or in a test). A `{ current: null }`
 * ref resolves to a `null` IntersectionObserver root — i.e. viewport-relative,
 * the safe default.
 *
 * @type {React.RefObject<HTMLElement|null>}
 */
const VIEWPORT_FALLBACK = { current: null };

/**
 * ScrollContainerContext — supplies the nearest scroll container's ref to the
 * fragments rendered inside it.
 *
 * The drilldown keeps one mounted body per visited item and shows/hides them,
 * so a single shared ref (the old `DrawerNavigationContext.scrollContainerRef`)
 * would be clobbered by the last body to render — hidden bodies' fragments would
 * then observe the wrong IntersectionObserver root. Each body instead provides
 * its own ref here, and `DrawerNavFragment` reads the nearest one, so every
 * fragment observes against the scroll container it actually lives in.
 *
 * @type {React.Context<React.RefObject<HTMLElement|null>>}
 */
const ScrollContainerContext = createContext(VIEWPORT_FALLBACK);

/**
 * Returns the nearest scroll container ref (viewport fallback when no provider
 * is present).
 *
 * @returns {React.RefObject<HTMLElement|null>}
 */
const useScrollContainer = () => useContext(ScrollContainerContext);

export { ScrollContainerContext, useScrollContainer };
